import React, { useCallback, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useIntl } from 'react-intl';
import queryString from 'query-string';
import { Route, useHistory, matchPath, Prompt } from 'react-router-dom';

import useBeforeunload from '../../hooks/useBeforeunload';
import { GET_CLAIMS } from '../../shared';
import {
  clearNewVitoloClaimFormData,
  relevantVitoloClaimsForList,
} from './claimFormData/newClaimFormData';
import { VITOLO_STEP_EXISTING_CLAIMS, VITOLO_STEP_MAIN } from './new/newClaimsFormSteps';
import { PARAM_CLAIM_ID, VITOLO_EDIT_STEP_MAIN } from './edit/editClaimFormSteps';
import {
  CLAIM_UPLOAD_FLOW_SOURCE_EXISTING_CLAIM_DOCUMENT,
  CLAIM_UPLOAD_FLOW_SOURCE_UPLOAD_DOCUMENT,
} from './claimUploadFlowSource';

import NewVitoloClaim from './new/NewVitoloClaim';
import EditVitoloClaim from './edit/EditVitoloClaim';

export default function VitoloClaims() {
  const intl = useIntl();
  const history = useHistory();

  const routeMatch = matchPath(history?.location?.pathname, {
    path: `${VITOLO_EDIT_STEP_MAIN}/${PARAM_CLAIM_ID}`,
  });

  const claimId =
    routeMatch && routeMatch.params[`${PARAM_CLAIM_ID.substring(1)}`]
      ? routeMatch.params[`${PARAM_CLAIM_ID.substring(1)}`]
      : '';

  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [claimSubmitted, setClaimSubmitted] = useState(false);
  const [isInitialPageLoad, setIsInitialPageLoad] = useState(true);

  const { data: claimsData } = useQuery(GET_CLAIMS);

  const relevantClaims = relevantVitoloClaimsForList(claimsData?.claims || []);

  // Prompt for page refresh
  const alertUser = useCallback((ev) => {
    ev.preventDefault();
    ev.returnValue = ''; // eslint-disable-line no-param-reassign
  }, []);

  useBeforeunload(alertUser);

  useEffect(() => {
    if (isInitialPageLoad) {
      setIsInitialPageLoad(false);
      setSelectedDocuments([]);
      clearNewVitoloClaimFormData();

      const newClaimPageUrl = VITOLO_STEP_MAIN;
      const isNewClaimPage = history?.location?.pathname === newClaimPageUrl;

      const selectExistingClaimPageUrl = VITOLO_STEP_EXISTING_CLAIMS;
      const isSelectExistingClaimPage = history?.location?.pathname === selectExistingClaimPageUrl;

      const existingPageUrl = `${VITOLO_EDIT_STEP_MAIN}/${claimId}`;
      const isExistingClaimPage = history?.location?.pathname === existingPageUrl;

      const params = queryString.parse(history?.location?.search);
      const source = params?.source;

      if (!isNewClaimPage && !isExistingClaimPage && !isSelectExistingClaimPage) {
        if (!!claimId && source === CLAIM_UPLOAD_FLOW_SOURCE_UPLOAD_DOCUMENT) {
          history.push(`${existingPageUrl}${history?.location?.search || ''}`);
        } else if (source === CLAIM_UPLOAD_FLOW_SOURCE_EXISTING_CLAIM_DOCUMENT) {
          history.push(`${selectExistingClaimPageUrl}${history?.location?.search || ''}`);
        } else if (source) {
          history.push(`${newClaimPageUrl}${history?.location?.search || ''}`);
        } else {
          history.push('/user-claims-info');
        }
      }
    }
  }, [isInitialPageLoad, history, claimId]);

  const showPrompt = !(claimSubmitted || selectedDocuments?.length < 1);

  return (
    <>
      {/* Prompt user data will be lost if navigate outside of the claims flow */}
      <Prompt
        when={showPrompt}
        message={({ pathname }) =>
          matchPath(pathname, { path: VITOLO_STEP_MAIN }) ||
          matchPath(pathname, { path: `${VITOLO_EDIT_STEP_MAIN}/${PARAM_CLAIM_ID}` })
            ? true
            : intl.formatMessage({ id: 'new_claim.prompt.message' })
        }
      />

      <Route path={VITOLO_STEP_MAIN}>
        <NewVitoloClaim
          selectedDocuments={selectedDocuments}
          setSelectedDocuments={setSelectedDocuments}
          setClaimSubmitted={setClaimSubmitted}
          relevantClaims={relevantClaims}
        />
      </Route>

      <Route path={`${VITOLO_EDIT_STEP_MAIN}/${PARAM_CLAIM_ID}`}>
        <EditVitoloClaim
          selectedDocuments={selectedDocuments}
          setSelectedDocuments={setSelectedDocuments}
          setClaimSubmitted={setClaimSubmitted}
          relevantClaims={relevantClaims}
        />
      </Route>
    </>
  );
}
